// ── Athena Preference Learning ────────────────────────────────────────────────
// Learns usage patterns from message signals (separate from drift detection).
// Never alters the cognitive profile — only reads/writes preference counters.
// Storage: localStorage 'athena_preference_learning' and 'athena_preference_overrides'

const PREF_KEY     = 'athena_preference_learning';
const OVERRIDE_KEY = 'athena_preference_overrides';
const THRESHOLD    = 4;  // signals before showing an insight

// ── Signal detection ──────────────────────────────────────────────────────────
const SIGNAL_RE = {
  EXAMPLE_REQUEST:  /\b(d[aê]\s*um?\s*exemplo|por\s*exemplo|como\s*seria|na\s*pr[aá]tica|caso\s*real)\b/i,
  IMAGE_REQUEST:    /\b(visual|diagrama|imagem|figura|ilustra[çc][aã]o|esquema|mapa\s*mental)\b/i,
  SUMMARY_REQUEST:  /\b(resum[ao]|resuma|resumindo|s[íi]ntese|de\s*forma\s*curta|tl;?dr)\b/i,
  STEP_REQUEST:     /\b(passo\s*a\s*passo|etapas?|em\s*passos|numerados?|como\s*fa[çz]o|me\s*ensina)\b/i,
  SHORT_REQUEST:    /\b(curto|breve|r[aá]pido|s[oó]\s*o\s*essencial|direto\s*ao\s*ponto|sem\s*enrola[çc][aã]o)\b/i,
  DETAILED_REQUEST: /\b(detalh[ae]|aprofund[ae]|explica\s*mais|fala\s*mais|completo|mais\s*detalhado)\b/i,
};

const KEY_MAP = {
  EXAMPLE_REQUEST:  'exampleRequests',
  IMAGE_REQUEST:    'imageRequests',
  SUMMARY_REQUEST:  'summaryRequests',
  STEP_REQUEST:     'stepRequests',
  SHORT_REQUEST:    'shortResponses',
  DETAILED_REQUEST: 'detailedResponses',
};

function readPrefs() {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}'); } catch { return {}; }
}
function writePrefs(data) {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(data)); } catch {}
}

export function trackPreferenceSignal(profileId, message) {
  if (!profileId || !message) return;
  const text = message.trim();
  if (text.length < 6) return;

  const matched = Object.entries(SIGNAL_RE)
    .filter(([, re]) => re.test(text))
    .map(([key]) => key);
  if (!matched.length) return;

  const prefs = readPrefs();
  if (!prefs[profileId]) {
    prefs[profileId] = {
      exampleRequests: 0, imageRequests: 0, summaryRequests: 0,
      stepRequests: 0, shortResponses: 0, detailedResponses: 0,
    };
  }
  for (const sig of matched) {
    const k = KEY_MAP[sig];
    if (k) prefs[profileId][k] = (prefs[profileId][k] || 0) + 1;
  }
  writePrefs(prefs);
}

export function getPreferences(profileId) {
  return readPrefs()[profileId] || null;
}

// ── Preference overrides (user-confirmed from insights) ───────────────────────
export function setPreferenceOverride(profileId, signal, enabled) {
  try {
    const ov = JSON.parse(localStorage.getItem(OVERRIDE_KEY) || '{}');
    if (!ov[profileId]) ov[profileId] = {};
    ov[profileId][signal] = enabled;
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(ov));
  } catch {}
}

export function getPreferenceOverrides(profileId) {
  try {
    const ov = JSON.parse(localStorage.getItem(OVERRIDE_KEY) || '{}');
    return ov[profileId] || {};
  } catch { return {}; }
}

// ── Insight detection ─────────────────────────────────────────────────────────
export function checkInsightSuggestion(profileId) {
  const p  = getPreferences(profileId);
  if (!p) return null;
  const ov = getPreferenceOverrides(profileId);  // skip already accepted

  if (!ov.IMAGE_REQUEST && (p.imageRequests || 0) >= THRESHOLD) return {
    signal:  'IMAGE_REQUEST',
    message: 'Percebi que você costuma aprender melhor através de imagens. Deseja que eu priorize referências visuais?',
  };
  if (!ov.STEP_REQUEST && (p.stepRequests || 0) >= THRESHOLD) return {
    signal:  'STEP_REQUEST',
    message: 'Percebi que você prefere explicações estruturadas. Deseja que eu organize respostas futuras em passos?',
  };
  if (!ov.EXAMPLE_REQUEST && (p.exampleRequests || 0) >= THRESHOLD) return {
    signal:  'EXAMPLE_REQUEST',
    message: 'Percebi que você pede exemplos com frequência. Deseja que eu inclua exemplos práticos em todas as respostas?',
  };
  if (!ov.SHORT_REQUEST && (p.shortResponses || 0) >= THRESHOLD) return {
    signal:  'SHORT_REQUEST',
    message: 'Percebi que você prefere respostas diretas e concisas. Deseja que eu mantenha esse padrão?',
  };
  return null;
}

// ── Dismiss (session-scoped — reappears next session if still relevant) ────────
export function dismissInsight(profileId, signal) {
  try {
    const shown = JSON.parse(sessionStorage.getItem('athena_insights_shown') || '{}');
    shown[`${profileId}_${signal}`] = true;
    sessionStorage.setItem('athena_insights_shown', JSON.stringify(shown));
  } catch {}
}

export function isInsightDismissed(profileId, signal) {
  try {
    const shown = JSON.parse(sessionStorage.getItem('athena_insights_shown') || '{}');
    return !!shown[`${profileId}_${signal}`];
  } catch { return false; }
}
